import { useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminCoachLeave,
  type AdminCoachRow,
} from "api/adminApi";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  InfoRow,
  Input,
  Modal,
  NUMERIC,
  Toggle,
} from "pages/admin/ui";
import {
  EMPTY_LEAVE_FORM,
  announcementLabel,
  canEditLeave,
  pausedByLeave,
  changedFields,
  coachFormOf,
  leaveFormError,
  leaveFormOf,
  leaveLength,
  leaveStateOf,
  monthDayLabel,
  settingWarnings,
  type CoachForm,
  type LeaveForm,
} from "./coachFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

const errorMessageOf = (e: unknown) =>
  (e as AxiosError).response?.status === 403
    ? "권한이 없는 계정이에요"
    : FALLBACK_ERROR_MESSAGE;

// edit: 설정·휴가 목록 / confirm: 설정 저장 확인 / leaveSave·leaveDelete: 휴가 등록·수정·삭제 확인
type Step = "edit" | "confirm" | "leaveSave" | "leaveDelete";

// 열려 있는 휴가 폼: original 이 없으면 새 휴가, 있으면 그 휴가를 고친다
type LeaveEdit = { original: AdminCoachLeave | null; form: LeaveForm };

const toCount = (value: string) => Math.max(0, Math.floor(Number(value) || 0));

/**
 * 코치 상세: 배정 설정(전체 캡·일일 캡·마스터 플랜·활성)과 휴가.
 * 설정은 폼 전체를 한 번에 저장하고, 마스터 플랜·활성이 바뀌거나 캡을 담당 인원 밑으로 내리면 확인 화면을 한 번 더 거친다.
 * 휴가는 설정과 따로 즉시 등록·수정·삭제하고, 모두 확인 화면을 거친다 (공지일에 내담자 안내·이용 기간 연장이 자동으로 나간다).
 * 등록된 휴가는 눌러서 고치거나 지운다. 안내가 나가기 전(공지일 18시 전)까지만.
 * 저장·등록·삭제 후에는 onChanged 로 목록을 다시 받는다. row 가 새 값으로 바뀌어 들어온다.
 */
export default function CoachDetailModal({
  row,
  today,
  onChanged,
  onClose,
}: {
  row: AdminCoachRow;
  today: string;
  onChanged: () => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>("edit");
  const [form, setForm] = useState<CoachForm>(() => coachFormOf(row));
  // 목록을 다시 받으면(휴가 삭제가 일일 캡을 되돌린 경우 등) 손대지 않은 항목은 새 값으로 맞춘다
  const baseline = useRef(coachFormOf(row));
  useEffect(() => {
    const next = coachFormOf(row);
    const prevBase = baseline.current;
    baseline.current = next;
    setForm((prev) => ({
      assignCapCount:
        prev.assignCapCount === prevBase.assignCapCount
          ? next.assignCapCount
          : prev.assignCapCount,
      assignDailyCapCount:
        prev.assignDailyCapCount === prevBase.assignDailyCapCount
          ? next.assignDailyCapCount
          : prev.assignDailyCapCount,
      isMasterPlanExcluded:
        prev.isMasterPlanExcluded === prevBase.isMasterPlanExcluded
          ? next.isMasterPlanExcluded
          : prev.isMasterPlanExcluded,
      isActive:
        prev.isActive === prevBase.isActive ? next.isActive : prev.isActive,
    }));
  }, [row]);
  const [leaveEdit, setLeaveEdit] = useState<LeaveEdit | null>(null);
  const [leaveTouched, setLeaveTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 휴가 수정 가능 여부는 공지일 11시·18시 경계를 본다
  const hour = new Date().getHours();

  const changes = changedFields(row, form);
  const warnings = settingWarnings(row, form);
  const subject = `${row.name} · ${row.email}`;

  const run = async (
    call: () => Promise<{ code: number; message?: string }>,
    onDone: () => void
  ) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await call();
      if (res.code === 0) {
        onChanged();
        onDone();
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(errorMessageOf(e));
    } finally {
      setSubmitting(false);
    }
  };

  const goTo = (next: Step) => {
    setError(null);
    setStep(next);
  };

  // 바뀐 항목만 보낸다
  const saveSetting = () =>
    run(
      () =>
        adminApi.coachUpdate({
          coachId: row.coachId,
          ...Object.fromEntries(changes.map((f) => [f.key, form[f.key]])),
        }),
      onClose
    );

  // 확인 대상(마스터 플랜·활성)이 바뀌었거나 안내할 영향이 있으면 확인 화면, 아니면 바로 저장
  const requestSave = () => {
    if (changes.some((f) => f.important) || warnings.length > 0)
      goTo("confirm");
    else saveSetting();
  };

  // 휴가 기간을 고르면 복귀일·공지일·연장 일수를 서버 기본값(영업일 기준)으로 채운다. 늦게 온 응답은 버린다
  const defaultSeq = useRef(0);
  const changeLeave = async (key: keyof LeaveForm, value: string) => {
    if (!leaveEdit) return;
    const next = { ...leaveEdit.form, [key]: value };
    setLeaveEdit({ ...leaveEdit, form: next });
    const { leaveFromDate: from, leaveToDate: to } = next;
    if (
      (key !== "leaveFromDate" && key !== "leaveToDate") ||
      !from ||
      !to ||
      to < from
    )
      return;
    const seq = ++defaultSeq.current;
    try {
      const res = await adminApi.coachLeaveDefault(from, to);
      if (seq !== defaultSeq.current || res.code !== 0 || !res.result) return;
      const { returnDate, announcementDate, bonusDays } = res.result;
      setLeaveEdit((prev) =>
        prev
          ? {
              ...prev,
              form: {
                ...prev.form,
                returnDate,
                announcementDate,
                bonusDays: `${bonusDays}`,
              },
            }
          : prev
      );
    } catch (e) {
      // 기본값만 비어 있을 뿐 직접 입력할 수 있다
    }
  };

  const leaveError = leaveEdit
    ? leaveFormError(leaveEdit.form, row.leaves, today, leaveEdit.original)
    : null;

  const openLeave = (original: AdminCoachLeave | null) => {
    setLeaveEdit({
      original,
      form: original ? leaveFormOf(original) : EMPTY_LEAVE_FORM,
    });
    setLeaveTouched(false);
  };

  const requestLeaveSave = () => {
    setLeaveTouched(true);
    if (!leaveError) goTo("leaveSave");
  };

  const closeLeave = () => {
    setLeaveEdit(null);
    setLeaveTouched(false);
  };

  if (step === "confirm") {
    const deactivating = row.isActive && !form.isActive;
    return (
      <Modal
        title="변경 내용을 확인해 주세요"
        subject={subject}
        width="560px"
        onClose={() => goTo("edit")}
        footer={
          <>
            <Button onClick={() => goTo("edit")}>돌아가기</Button>
            <Button
              variant={deactivating ? "danger" : "primary"}
              loading={submitting}
              loadingLabel="저장 중"
              onClick={saveSetting}
            >
              {deactivating ? "비활성으로 저장할게요" : "저장할게요"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="px-3 py-2 text-xs font-semibold text-slate-500">
                    항목
                  </th>
                  <th className="px-3 py-2 text-xs font-semibold text-slate-500">
                    변경 전
                  </th>
                  <th className="px-3 py-2 text-xs font-semibold text-slate-500">
                    변경 후
                  </th>
                </tr>
              </thead>
              <tbody>
                {changes.map((f) => (
                  <tr
                    key={f.key}
                    className="border-b border-slate-100 last:border-b-0"
                  >
                    <td className="px-3 py-2 text-slate-500">{f.label}</td>
                    <td
                      className={`px-3 py-2 text-slate-400 line-through decoration-slate-300 ${NUMERIC}`}
                    >
                      {f.format(coachFormOf(row)[f.key])}
                    </td>
                    <td
                      className={`px-3 py-2 font-medium text-slate-900 ${NUMERIC}`}
                    >
                      {f.format(form[f.key])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {warnings.map((w) => (
            <p
              key={w}
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700"
            >
              {w}
            </p>
          ))}
          <p className="text-sm text-slate-600">
            저장하면 다음 배정부터 바로 적용돼요.
          </p>
          {error && <ErrorBanner>{error}</ErrorBanner>}
        </div>
      </Modal>
    );
  }

  if ((step === "leaveSave" || step === "leaveDelete") && leaveEdit) {
    const { original } = leaveEdit;
    const isDelete = step === "leaveDelete";
    const leave: AdminCoachLeave =
      isDelete && original
        ? original
        : { ...leaveEdit.form, bonusDays: Number(leaveEdit.form.bonusDays) };
    // 수정이면 바뀐 값 옆에 원래 값을 흐리게 둔다
    const was = (value: string, previous: string | null) =>
      !isDelete && original && previous != null && `${previous}` !== value ? (
        <span className="ml-1.5 text-slate-400 line-through decoration-slate-300">
          {previous}
        </span>
      ) : null;
    const title = isDelete
      ? "휴가를 삭제할까요?"
      : original
        ? "휴가를 수정할까요?"
        : "휴가를 등록할까요?";
    const save = () =>
      run(
        () =>
          isDelete && original
            ? adminApi.coachLeaveDelete(row.coachId, original.leaveFromDate)
            : original
              ? adminApi.coachLeaveUpdate(
                  row.coachId,
                  original.leaveFromDate,
                  leave
                )
              : adminApi.coachLeaveCreate(row.coachId, leave),
        () => {
          closeLeave();
          goTo("edit");
        }
      );
    return (
      <Modal
        title={title}
        subject={subject}
        width="560px"
        onClose={() => goTo("edit")}
        footer={
          <>
            <Button onClick={() => goTo("edit")}>돌아가기</Button>
            <Button
              variant={isDelete ? "danger" : "primary"}
              loading={submitting}
              loadingLabel="처리 중"
              onClick={save}
            >
              {isDelete ? "삭제할게요" : original ? "수정할게요" : "등록할게요"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 rounded-lg bg-slate-50 px-4 py-3">
            <InfoRow label="휴가">
              <span className={NUMERIC}>
                {monthDayLabel(leave.leaveFromDate)} ~{" "}
                {monthDayLabel(leave.leaveToDate)}
              </span>{" "}
              <span className="text-slate-400">({leaveLength(leave)}일)</span>
              {was(
                `${monthDayLabel(leave.leaveFromDate)} ~ ${monthDayLabel(leave.leaveToDate)}`,
                original &&
                  `${monthDayLabel(original.leaveFromDate)} ~ ${monthDayLabel(original.leaveToDate)}`
              )}
            </InfoRow>
            <InfoRow label="복귀일">
              {monthDayLabel(leave.returnDate)}
              {was(
                monthDayLabel(leave.returnDate),
                original && monthDayLabel(original.returnDate)
              )}
            </InfoRow>
            <InfoRow label="안내 채팅">
              <span className="font-medium text-[#0F55A7]">
                {announcementLabel(leave.announcementDate)}
              </span>
              {was(
                announcementLabel(leave.announcementDate),
                original && announcementLabel(original.announcementDate)
              )}
            </InfoRow>
            <InfoRow label="이용 기간 연장">
              {leave.bonusDays}일
              {was(
                `${leave.bonusDays}일`,
                original && `${original.bonusDays}일`
              )}
            </InfoRow>
          </div>
          {/* 공지일 11시가 지나 이미 신규 배정이 멈춘 휴가를 지우거나 공지일을 옮기면 서버가 일일 캡을 50 으로 되돌린다 */}
          {original &&
            pausedByLeave(original, today, hour) &&
            (isDelete ||
              leave.announcementDate !== original.announcementDate) && (
              <p className="rounded-md border border-[#0F55A7]/20 bg-[#DCEFFA] px-3 py-2 text-sm text-[#0F55A7]">
                오늘 11시부터 멈춘 신규 배정을 다시 열어요 (일일 캡 50).
              </p>
            )}
          {isDelete ? (
            <p className="text-sm leading-relaxed text-slate-600">
              아직 안내 전이라 내담자에게 나간 채팅은 없어요. 삭제하면 안내·이용
              기간 연장이 모두 실행되지 않아요.
            </p>
          ) : (
            <>
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                {announcementLabel(leave.announcementDate)}에 담당 내담자(지금{" "}
                {row.assignedPatientCount}명)에게 휴가 안내 채팅이 자동으로
                나가고, 이용 기간이 {leave.bonusDays}일씩 늘어나요.
              </p>
              <p className="text-sm leading-relaxed text-slate-600">
                공지일 11시부터 신규 배정이 멈추고(일일 캡 0), 휴가 끝난 다음날
                0시에 다시 받아요(일일 캡 50). 안내가 나가기 전(공지일 18시
                전)까지는 고치거나 지울 수 있어요.
              </p>
            </>
          )}
          {error && <ErrorBanner>{error}</ErrorBanner>}
        </div>
      </Modal>
    );
  }

  const capBelowAssigned =
    form.assignCapCount > 0 && form.assignCapCount < row.assignedPatientCount;

  return (
    <Modal
      title="코치 상세"
      subject={subject}
      width="560px"
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto text-xs text-slate-400">
            {changes.length ? `${changes.length}개 항목 변경됨` : "변경 없음"}
          </span>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            disabled={changes.length === 0}
            loading={submitting}
            loadingLabel="저장 중"
            onClick={requestSave}
          >
            저장
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 rounded-lg bg-slate-50 px-4 py-3">
          <InfoRow label="이름">{row.name}</InfoRow>
          <InfoRow label="코치 ID">{row.coachId}</InfoRow>
          <InfoRow label="활동 기간">
            {row.startDate ?? "–"} ~ {row.endDate ?? ""}
          </InfoRow>
          <InfoRow label="담당 인원">{row.assignedPatientCount}명</InfoRow>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold text-slate-500">캡</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="전체 캡"
              helper={
                capBelowAssigned ? (
                  <span className="text-amber-700">
                    담당 인원 {row.assignedPatientCount}명보다 작아요. 담당
                    인원이 줄어들 때까지 신규 배정을 받지 않아요.
                  </span>
                ) : form.assignCapCount === 0 ? (
                  "0이면 자동 배정을 받지 않아요 (초과 배정에서도 빠져요)."
                ) : (
                  `담당 인원 ${row.assignedPatientCount}명 · 남은 자리 ${
                    form.assignCapCount - row.assignedPatientCount
                  }명`
                )
              }
            >
              <Input
                type="number"
                min={0}
                step={1}
                className={`${NUMERIC} ${capBelowAssigned ? "border-amber-400" : ""}`}
                value={form.assignCapCount}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    assignCapCount: toCount(e.target.value),
                  }))
                }
              />
            </Field>
            <Field
              label="일일 캡"
              helper={
                form.assignDailyCapCount === 0 ? (
                  <span className="text-amber-700">
                    0이면 신규 배정이 멈춰요. 휴가가 있으면 공지일 11시에 0,
                    휴가 끝난 다음날 0시에 50으로 자동으로 바뀌어요.
                  </span>
                ) : (
                  "하루에 새로 받는 최대 인원. 휴가가 있으면 자동으로 0 ↔ 50 이 돼요."
                )
              }
            >
              <Input
                type="number"
                min={0}
                step={1}
                className={NUMERIC}
                value={form.assignDailyCapCount}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    assignDailyCapCount: toCount(e.target.value),
                  }))
                }
              />
            </Field>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold text-slate-500">배정 조건</p>
          <Toggle
            checked={!form.isMasterPlanExcluded}
            onChange={(next) =>
              setForm((prev) => ({ ...prev, isMasterPlanExcluded: !next }))
            }
            label="마스터 플랜 담당"
            helper="끄면 마스터 플랜 내담자는 이 코치에게 배정되지 않아요."
          />
          <Toggle
            checked={form.isActive}
            onChange={(next) =>
              setForm((prev) => ({ ...prev, isActive: next }))
            }
            label="활성"
            helper="끄면 자동 배정·재배정 후보에서 빠지고 실무 목록 기본 화면에서 숨겨져요."
          />
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-500">휴가</p>
            {!leaveEdit && (
              <button
                type="button"
                className="text-sm font-medium text-[#0F55A7] hover:underline"
                onClick={() => openLeave(null)}
              >
                + 휴가 추가
              </button>
            )}
          </div>
          {row.leaves.length === 0 && !leaveEdit && (
            <p className="text-sm text-slate-400">등록된 휴가가 없어요.</p>
          )}
          {row.leaves.map((leave) =>
            leaveEdit?.original?.leaveFromDate === leave.leaveFromDate ? (
              <LeaveFormPanel
                key={leave.leaveFromDate}
                edit={leaveEdit}
                error={leaveTouched ? leaveError : null}
                onChange={changeLeave}
                onCancel={closeLeave}
                onSave={requestLeaveSave}
                onDelete={() => goTo("leaveDelete")}
              />
            ) : (
              <LeaveItem
                key={leave.leaveFromDate}
                leave={leave}
                today={today}
                // 다른 휴가 폼이 열려 있으면 먼저 닫게 한다. 입력 중인 값이 조용히 사라지지 않게
                onOpen={leaveEdit ? undefined : () => openLeave(leave)}
              />
            )
          )}
          {leaveEdit && !leaveEdit.original && (
            <LeaveFormPanel
              edit={leaveEdit}
              error={leaveTouched ? leaveError : null}
              onChange={changeLeave}
              onCancel={closeLeave}
              onSave={requestLeaveSave}
            />
          )}
        </div>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

// 등록된 휴가 한 건. 안내 전(공지일 18시 전)까지는 눌러서 고치거나 지울 수 있다
function LeaveItem({
  leave,
  today,
  onOpen,
}: {
  leave: AdminCoachLeave;
  today: string;
  onOpen?: () => void;
}) {
  const editable = canEditLeave(leave, today, new Date().getHours());
  const clickable = editable && onOpen != null;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onOpen}
      className={`flex w-full flex-col gap-1.5 rounded-md border border-slate-200 px-3 py-2.5 text-left transition-colors ${
        clickable
          ? "hover:border-slate-300 hover:bg-slate-50"
          : "cursor-default"
      }`}
    >
      <span className="flex flex-wrap items-center gap-1.5 text-sm text-slate-900">
        {leaveStateOf(leave, today) === "on" ? (
          <Badge tone="warn">휴가 중</Badge>
        ) : (
          <Badge tone="idle">휴가 예정</Badge>
        )}
        <span className={`font-medium ${NUMERIC}`}>
          {monthDayLabel(leave.leaveFromDate)} ~{" "}
          {monthDayLabel(leave.leaveToDate)}
        </span>
        <span className="text-slate-400">
          · 복귀 {monthDayLabel(leave.returnDate)} · 이용 기간 {leave.bonusDays}
          일 연장
        </span>
      </span>
      <span className="flex flex-wrap items-center gap-1.5 text-xs">
        {editable ? (
          <Badge tone="info">
            안내 채팅 {announcementLabel(leave.announcementDate)}
          </Badge>
        ) : (
          <span className="text-slate-400">
            {monthDayLabel(leave.announcementDate)} 안내됨 · 수정·삭제할 수
            없어요
          </span>
        )}
      </span>
    </button>
  );
}

// 휴가 등록·수정 폼. 수정이면 왼쪽에 삭제가 붙는다
function LeaveFormPanel({
  edit,
  error,
  onChange,
  onCancel,
  onSave,
  onDelete,
}: {
  edit: LeaveEdit;
  error: string | null;
  onChange: (key: keyof LeaveForm, value: string) => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
}) {
  const { form, original } = edit;
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-[#0F55A7]/30 px-4 py-4">
      <p className="text-xs text-slate-500">
        휴가 기간을 고르면 나머지는 영업일(주말·공휴일 제외) 기준으로 자동으로
        채워져요. 바꿔도 돼요.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="휴가 시작">
          <Input
            type="date"
            value={form.leaveFromDate}
            onChange={(e) => onChange("leaveFromDate", e.target.value)}
          />
        </Field>
        <Field label="휴가 종료">
          <Input
            type="date"
            value={form.leaveToDate}
            min={form.leaveFromDate || undefined}
            onChange={(e) => onChange("leaveToDate", e.target.value)}
          />
        </Field>
        <Field label="복귀일" helper="기본: 휴가 종료 다음 영업일">
          <Input
            type="date"
            value={form.returnDate}
            onChange={(e) => onChange("returnDate", e.target.value)}
          />
        </Field>
        <Field
          label="공지일"
          helper={
            form.announcementDate ? (
              <span className="font-medium text-[#0F55A7]">
                {announcementLabel(form.announcementDate)} 안내 채팅 · 11시부터
                신규 배정 중단
              </span>
            ) : (
              "기본: 휴가 시작 2영업일 전"
            )
          }
        >
          <Input
            type="date"
            value={form.announcementDate}
            onChange={(e) => onChange("announcementDate", e.target.value)}
          />
        </Field>
        <Field label="이용 기간 연장 (일)" helper="기본: 휴가 기간의 영업일 수">
          <Input
            type="number"
            min={1}
            step={1}
            className={NUMERIC}
            value={form.bonusDays}
            onChange={(e) => onChange("bonusDays", e.target.value)}
          />
        </Field>
      </div>
      {error && <p className="text-xs text-rose-600">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        {original && onDelete && (
          <Button variant="danger" className="mr-auto" onClick={onDelete}>
            삭제
          </Button>
        )}
        <Button onClick={onCancel}>취소</Button>
        <Button variant="primary" onClick={onSave}>
          {original ? "저장" : "등록"}
        </Button>
      </div>
    </div>
  );
}
